import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $accountByIdStore, $currenciesStore, $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, RoutePaths } from '@shared/utils';
import { Managers, Sidebar } from './widgets';
import { AccountTileBig, Cards, List as Transactions, PageContainer, View } from '@widgets';

export const AccountPage = () => {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const { navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const accountStore = useUnit($initialAccountsStore.store);
  const accountDetailsStore = useUnit($accountByIdStore.store);

  const [selectedAccountId, setSelectedAccountId] = useState<number>(id);

  const { t } = useTranslation('account');

  useEffect(() => {
    $currenciesStore.events.getCurrencies();
  }, []);

  useEffect(() => {
    if (!profileFeatureStore.data?.accountFlows?.length) navigate(RoutePaths.dashboard.onNavigate());
    if (!selectedAccountId) return;
    $accountByIdStore.events.getAccount({ id: selectedAccountId });
    return () => $accountByIdStore.events.clear();
  }, [selectedAccountId]);

  useEffect(() => {
    if (accountDetailsStore.loading) return;
    if (accountDetailsStore.error !== 'Action is forbidden.') return;
    $accountByIdStore.events.clear();
    navigate(RoutePaths.error.onNavigate(), { replace: true });
  }, [accountDetailsStore.loading, accountDetailsStore.error]);

  const mainAccount = useMemo(() => {
    if (!accountDetailsStore.data || !accountStore.data) return;
    const { mainAccId } = accountDetailsStore.data;
    return mainAccId === null ? accountDetailsStore.data : accountStore.data.find((account) => account.id === mainAccId);
  }, [accountDetailsStore.data, accountStore.data]);
  const subAccounts = useMemo(() => accountStore.data.filter((account) => account.mainAccId === mainAccount?.id), [accountStore.data, mainAccount]);
  const accountIds = useMemo(() => [selectedAccountId], [selectedAccountId]);
  return (
    <PageContainer title={t('title')} back backText={t('backText')}>
      <View
        loading={accountDetailsStore.loading}
        error={accountDetailsStore.error}
        onRefresh={() => $accountByIdStore.events.getAccount({ id: selectedAccountId! })}
        noData={!accountDetailsStore.data}
      >
        {accountDetailsStore.data && (
          <main className={cn('flex gap-6', 'max-[1440px]:flex-col')}>
            <div className='flex flex-1 flex-col gap-6'>
              <AccountTileBig
                account={accountDetailsStore.data}
                mainAccount={mainAccount}
                subAccounts={subAccounts}
                selectedAccountId={selectedAccountId}
                onSelectAccount={(accountId) => setSelectedAccountId(accountId)}
              />
              {profileFeatureStore.data?.userManagement?.viewUsers && <Managers accountId={selectedAccountId!} />}
              <Cards title={t('card.title')} accountId={selectedAccountId} />
              <Transactions accountType={accountDetailsStore.data.type} accountIds={accountIds} filter search />
            </div>
            <Sidebar />
          </main>
        )}
      </View>
    </PageContainer>
  );
};
